const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimFaqPage extends HTMLElement {
  #activeTabId = '';
  #panels = [];
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #tabButtons = [];

  connectedCallback() {
    this.#tabButtons = Array.from(this.querySelectorAll('[data-darkrim-faq-page-tab]'));
    this.#panels = Array.from(this.querySelectorAll('[data-darkrim-faq-page-panel]'));

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#panels.forEach((panel) => this.#syncPanelItems(panel, { immediate: true }));

    const defaultTabId =
      this.#tabButtons.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.tabId ||
      this.#tabButtons[0]?.dataset.tabId ||
      this.#panels[0]?.dataset.tabId ||
      '';

    this.#selectTab(defaultTabId, { immediate: true, focus: false });
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-darkrim-faq-page-tab]');
    if (tab && this.contains(tab)) {
      this.#selectTab(tab.dataset.tabId, { focus: false });
      return;
    }

    const trigger = event.target.closest('[data-darkrim-faq-page-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const item = trigger.closest('[data-darkrim-faq-page-item]');
    if (item) this.#toggleItem(item);
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-darkrim-faq-page-tab]');
    if (!tab || !this.contains(tab) || this.#tabButtons.length === 0) return;

    const currentIndex = this.#tabButtons.indexOf(tab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % this.#tabButtons.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + this.#tabButtons.length) % this.#tabButtons.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.#tabButtons.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.#selectTab(this.#tabButtons[nextIndex].dataset.tabId, { focus: true });
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-darkrim-faq-page-item]');
    const panel = event.target.closest('[data-darkrim-faq-page-panel]');

    if (item && panel) {
      this.#selectTab(panel.dataset.tabId, { immediate: true, focus: false });
      this.#openItem(item, { immediate: true });
      return;
    }

    if (panel) {
      this.#selectTab(panel.dataset.tabId, { immediate: true, focus: false });
    }
  };

  #selectTab(tabId, options = {}) {
    if (!tabId) return;

    this.#activeTabId = tabId;

    this.#tabButtons.forEach((tab) => {
      const selected = tab.dataset.tabId === tabId;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      tab.classList.toggle('is-active', selected);
      if (selected && options.focus) tab.focus();
    });

    this.#panels.forEach((panel) => {
      const selected = panel.dataset.tabId === tabId;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
    });
  }

  #toggleItem(item) {
    const panel = item.closest('[data-darkrim-faq-page-panel]');
    if (!panel) return;

    const shouldOpen = !item.classList.contains('is-open');
    this.#itemsInPanel(panel).forEach((entry) => {
      this.#setItemOpen(entry, entry === item && shouldOpen);
    });
  }

  #openItem(item, options = {}) {
    const panel = item.closest('[data-darkrim-faq-page-panel]');
    if (!panel) return;

    this.#itemsInPanel(panel).forEach((entry) => {
      this.#setItemOpen(entry, entry === item, options);
    });
  }

  #syncPanelItems(panel, options = {}) {
    const items = this.#itemsInPanel(panel);
    items.forEach((item, index) => {
      this.#setItemOpen(item, index === 0, options);
    });
  }

  #itemsInPanel(panel) {
    return Array.from(panel.querySelectorAll('[data-darkrim-faq-page-item]'));
  }

  #setItemOpen(item, isOpen, options = {}) {
    const trigger = item.querySelector('[data-darkrim-faq-page-trigger]');
    const answer = item.querySelector('[data-darkrim-faq-page-answer]');

    item.classList.toggle('is-open', isOpen);
    if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
    if (!answer) return;

    if (isOpen) {
      this.#openAnswer(answer, options.immediate);
    } else {
      this.#closeAnswer(answer, options.immediate);
    }
  }

  #openAnswer(panel, immediate) {
    panel.dataset.darkrimFaqPageState = 'opening';
    panel.hidden = false;

    if (immediate || this.#reducedMotionQuery.matches) {
      panel.style.height = 'auto';
      panel.dataset.darkrimFaqPageState = 'open';
      return;
    }

    panel.style.height = '0px';
    panel.offsetHeight;
    panel.style.height = `${panel.scrollHeight}px`;

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;

      panel.removeEventListener('transitionend', handleTransitionEnd);
      if (panel.dataset.darkrimFaqPageState !== 'opening') return;

      panel.style.height = 'auto';
      panel.dataset.darkrimFaqPageState = 'open';
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  #closeAnswer(panel, immediate) {
    if (panel.hidden && !panel.style.height) return;

    panel.dataset.darkrimFaqPageState = 'closing';

    if (immediate || this.#reducedMotionQuery.matches) {
      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqPageState = 'closed';
      return;
    }

    const startHeight = panel.scrollHeight;
    if (startHeight === 0) {
      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqPageState = 'closed';
      return;
    }

    panel.style.height = `${startHeight}px`;
    panel.offsetHeight;
    panel.style.height = '0px';

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;

      panel.removeEventListener('transitionend', handleTransitionEnd);
      if (panel.dataset.darkrimFaqPageState !== 'closing') return;

      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqPageState = 'closed';
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }
}

if (!customElements.get('darkrim-faq-page')) {
  customElements.define('darkrim-faq-page', DarkrimFaqPage);
}
